// Globaler Scan-Dialog (Topbar „Scannen“, Mitte der unteren Leiste): Kamera oder Handscanner lesen einen Code,
// die Scan-Ziele der Module (Manifest: scanHandlers) entscheiden, wohin er führt. Ein Ziel → direkt dorthin,
// mehrere → Auswahl, keines → Übergabe an die Suche. Jeder Scan ist auch per Eingabe möglich.
import { ArrowRight, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useWebModules } from '../api/context'
import { useSession } from '../api/session'
import { useScanInput, type ScanResult } from '../lib/scan'
import { shell, useShellStore } from '../stores/shell'
import { BarcodeScanner, type ScanFeedback } from '../ui/BarcodeScanner'
import { Button } from '../ui/Button'
import { CodeText } from '../ui/format'
import { Modal } from '../ui/Modal'
import { resolveScan, type ScanTarget } from './scan'

interface Outcome {
  code: string
  targets: ScanTarget[]
}

export function ScanDialog() {
  const open = useShellStore((state) => state.scanOpen)
  const modules = useWebModules()
  const session = useSession()
  const navigate = useNavigate()
  const [outcome, setOutcome] = useState<Outcome | null>(null)

  useEffect(() => {
    if (!open) setOutcome(null)
  }, [open])

  const go = (to: string): void => {
    shell.setScan(false)
    void navigate(to)
  }

  const handle = (result: ScanResult): ScanFeedback => {
    const targets = resolveScan(result, modules, session)
    setOutcome({ code: result.code, targets })
    if (targets.length === 1 && targets[0]) {
      go(targets[0].to)
      return { status: 'ok', message: targets[0].label }
    }
    if (targets.length > 1) return { status: 'ok', message: 'Bitte wählen Sie, was Sie mit dem Code tun möchten.' }
    return { status: 'unknown', message: 'Für diesen Code ist hier noch kein Ziel hinterlegt.' }
  }

  // Handscanner: schnelle Tastenfolge mit Enter – unabhängig vom Fokus, solange der Dialog offen ist
  useScanInput((result) => void handle(result), { enabled: open, captureInInputs: false })

  return (
    <Modal
      open={open}
      onOpenChange={shell.setScan}
      title="Scannen"
      description="Halten Sie den Barcode vor die Kamera, nutzen Sie den Handscanner oder tippen Sie den Code ein."
      size="md"
      data-testid="scan-dialog"
    >
      <div className="flex flex-col gap-4">
        <BarcodeScanner
          active={open && (outcome === null || outcome.targets.length === 0)}
          mode="continuous"
          onDetect={handle}
        />
        {outcome && outcome.targets.length !== 1 && (
          <section className="flex flex-col gap-2" aria-live="polite">
            <p className="text-dense text-fg-muted">
              Gelesen: <CodeText muted={false}>{outcome.code}</CodeText>
            </p>
            {outcome.targets.map((target) => (
              <Button key={target.key} fullWidth iconEnd={ArrowRight} onClick={() => go(target.to)}>
                {target.label}
              </Button>
            ))}
            {outcome.targets.length === 0 && (
              <Button
                fullWidth
                icon={Search}
                onClick={() => {
                  shell.setScan(false)
                  shell.openPalette(outcome.code)
                }}
              >
                In der Suche öffnen
              </Button>
            )}
            {outcome.targets.length > 1 && (
              <Button variant="ghost" onClick={() => setOutcome(null)}>
                Anderen Code scannen
              </Button>
            )}
          </section>
        )}
      </div>
    </Modal>
  )
}
